package fr.frux.feature.treedetail

import androidx.compose.ui.graphics.vector.ImageVector
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.designsystem.components.UserModerationMessage
import fr.frux.core.designsystem.components.UserReportReason
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.media.PickedImage
import fr.frux.core.media.prepareForUpload
import fr.frux.feature.treedetail.identity.IdentityUi
import fr.frux.feature.treedetail.identity.TreeIdentityController
import fr.frux.feature.treedetail.photos.PendingPhotosController
import fr.frux.feature.treedetail.site.TreeSiteLinkController
import fr.frux.shared.domain.favorites.FavoritesStore
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.HarvestStatus
import fr.frux.shared.domain.model.ObservableTreeStatus
import fr.frux.shared.domain.model.SpeciesLookalike
import fr.frux.shared.domain.model.SpeciesPhoto
import fr.frux.shared.domain.model.TreeCorrectionMark
import fr.frux.shared.domain.model.TreeObservation
import fr.frux.shared.domain.model.TreeTrait
import fr.frux.shared.domain.model.TreeTraitKind
import fr.frux.shared.domain.model.currentHarvestMonth
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.TreeRepository
import fr.frux.shared.domain.repository.UserRepository
import fr.frux.shared.presentation.TreeSighting
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * ViewModel for Tree Detail screen.
 *
 * Manages:
 * - Tree details with full information
 * - Emoji reactions with toggle
 * - Report functionality
 * - Start Harvest action
 * - Creator profile navigation
 *
 * ViewModel d'écran d'agrégat, découpage non souhaité à cette taille — cf. #108/#111.
 */
@Suppress("TooManyFunctions")
@OptIn(ExperimentalTime::class)
class TreeDetailViewModel(
    private val treeId: Long,
    private val treeRepository: TreeRepository,
    private val speciesRepository: SpeciesRepository,
    private val favoritesStore: FavoritesStore,
    private val harvestRepository: HarvestRepository,
    /**
     * Provider lu à chaque usage, pas un snapshot (#264) : ce VM survit à un login déclenché
     * depuis sa propre invite de connexion (Nav3 réutilise le ViewModelStore de l'entrée
     * TreeDetail à la reprise post-`onAuthenticated()`, la Composable n'est jamais recréée) — un
     * `Long?` figé à la construction resterait `null` après connexion. Gouverne "Signaler un
     * doute" (masqué sur son propre arbre) et "Maintenir mon identification" (créateur seul).
     * `null` pour un visiteur anonyme.
     */
    private val currentUserIdProvider: () -> Long? = { null },
    /** Horloge lue à chaque chargement, jamais figée (#420) : pilotable en test sans horloge système. */
    private val now: () -> Instant = { Clock.System.now() },
    /**
     * Gestes de l'ADR 0029 (#515) sur l'auteur. Nullable avec défaut pour ne pas réécrire les
     * constructions de test qui n'exercent pas ce chemin : le composition root le passe toujours.
     */
    private val userRepository: UserRepository? = null,
    editing: TreeDetailEditing? = null,
) : ViewModel() {
    companion object {
        private const val TAG = "TreeDetailViewModel"
    }

    private val _state = MutableStateFlow(TreeDetailState())
    val state: StateFlow<TreeDetailState> = _state.asStateFlow()

    /** Événements one-shot vers l'UI (#224) : la suppression navigue en arrière, la fiche est détruite. */
    private val _events = Channel<TreeDetailEvent>(Channel.BUFFERED)
    val events: Flow<TreeDetailEvent> = _events.receiveAsFlow()

    val identity =
        TreeIdentityController(
            treeId = treeId,
            corrections = editing?.corrections ?: UnavailableTreeCorrections,
            speciesRepository = speciesRepository,
            scope = viewModelScope,
            state = _state,
            isLoggedIn = { currentUserIdProvider() != null },
            onLoginRequired = { _events.trySend(TreeDetailEvent.LoginRequired) },
            reload = ::loadTreeDetail,
            newKey = editing?.newKey ?: { "" },
        )

    val pendingPhotos =
        PendingPhotosController(
            treeId = treeId,
            uploads = editing?.photoUploads,
            scope = viewModelScope,
            state = _state,
            reload = ::loadTreeDetail,
        )

    val siteLink = TreeSiteLinkController(editing?.sites, viewModelScope, currentUserIdProvider)

    init {
        loadTreeDetail()
        loadHarvests()
        // Source unique du favori (chantier favoris carte) : la fiche reflète tout toggle posé
        // ailleurs (carte, autre onglet) sans dépendre d'un rechargement du détail.
        viewModelScope.launch {
            favoritesStore.favoriteIds.collect { ids ->
                _state.update { it.copy(isFavorite = treeId in ids) }
            }
        }
    }

    /**
     * Retry loading the tree details (used by [fr.frux.core.designsystem.components.ErrorState]).
     */
    fun retry() = loadTreeDetail()

    /** `true` après le premier appel à [onScreenResumed] — voir sa doc. */
    private var hasResumedBefore = false

    /**
     * Signal de remontée sur cet écran (#224), appelé par `TreeDetailScreen` depuis un
     * `LaunchedEffect(Unit)`. Ce `ViewModel` survit à la navigation vers un écran poussé par-dessus (retenu par le
     * `ViewModelStore` de Nav3 tant que cette route reste sur la pile) : son `init` ne se relance
     * pas au retour, contrairement à la Composable qui est détruite/recomposée à chaque passage au
     * sommet de la pile — il n'existe donc pas de mécanisme de rechargement automatique à la
     * remontée. [hasResumedBefore] distingue la toute première composition (où [init] a déjà
     * lancé le chargement, un second appel ferait un GET en double) de tout retour ultérieur sur
     * l'écran (retour d'un autre écran poussé par-dessus type
     * `CreateHarvest` — rechargement inutile mais inoffensif dans ce cas).
     */
    fun onScreenResumed() {
        if (hasResumedBefore) {
            loadTreeDetail()
            loadHarvests()
        } else {
            hasResumedBefore = true
        }
    }

    /**
     * Load tree details from repository.
     */
    private fun loadTreeDetail() {
        // Refresh silencieux (#249) : une fiche déjà affichée le reste pendant tout rechargement
        // (post-action réussie, remontée d'écran) — pas de LoadingState plein page, et un échec
        // réseau du refresh ne doit jamais recouvrir d'un ErrorState une action qui a réussi.
        val silent = _state.value.tree != null
        viewModelScope.launch {
            Logger.d(TAG, "Loading tree detail: id=$treeId (silent=$silent)")
            if (!silent) {
                _state.update { it.copy(isLoading = true, error = null, notFound = false) }
            }

            treeRepository
                .getTreeById(treeId)
                .collect { result ->
                    result.fold(
                        onSuccess = { domainTree ->
                            Logger.i(
                                TAG,
                                "✅ Loaded tree: ${domainTree.species.commonNameFr} (${domainTree.status})",
                            )
                            // Un échec du référentiel espèces ne doit pas mettre l'écran en erreur :
                            // l'arbre s'affiche quand même, simplement sans frise de saison ni
                            // dangerosité (absence d'avertissement = "non documenté", jamais "sûr").
                            val speciesEntry =
                                speciesRepository
                                    .getSpecies()
                                    // Pas de niveau warn dédié dans Logger (d/i/e) ; e() est réservé aux
                                    // échecs qui remontent à l'écran, ce qui n'est pas le cas ici.
                                    .onFailure { Logger.d(TAG, "⚠️ Référentiel espèces indisponible : ${it.message}") }
                                    .getOrNull()
                                    ?.find { it.id == domainTree.species.id }
                            val harvests = speciesEntry?.harvests.orEmpty()
                            val lookalikes = speciesEntry?.lookalikes.orEmpty()
                            val treeDetail =
                                domainTree.toTreeDetail(
                                    harvests = harvests,
                                    currentMonth = currentHarvestMonth(),
                                    lookalikes = lookalikes,
                                    speciesPhoto = speciesEntry?.photo,
                                    currentUserId = currentUserIdProvider(),
                                    now = now(),
                                )
                            // Le détail est la seule route qui connaît l'état favori serveur hors
                            // refresh du store : on l'y verse au lieu de le garder local.
                            favoritesStore.seed(treeId, domainTree.isFavorite)
                            siteLink.onTreeLoaded(domainTree)
                            _state.update {
                                it.copy(
                                    tree = treeDetail,
                                    isLoading = false,
                                    error = null,
                                )
                            }
                        },
                        onFailure = { error ->
                            if (!silent && error is AppError.NotFound) {
                                // Pas de Logger.e : un lien vers un arbre masqué ou supprimé est un
                                // parcours attendu, le pont vers Sentry (#175) en ferait un event.
                                Logger.i(TAG, "⚠️ Arbre $treeId introuvable ou masqué")
                                _state.update { it.copy(isLoading = false, notFound = true) }
                                return@fold
                            }
                            Logger.e(TAG, "❌ Failed to load tree", error)
                            _state.update {
                                if (silent) {
                                    // L'ancien contenu reste affiché ; l'échec du refresh se
                                    // signale discrètement (snackbar), jamais en plein écran.
                                    it.copy(refreshFailed = true)
                                } else {
                                    it.copy(
                                        isLoading = false,
                                        error = error.asAppError(),
                                    )
                                }
                            }
                        },
                    )
                }
        }
    }

    /**
     * Récoltes prévues pour l'arbre (#226), chargées en parallèle du détail — non bloquant, même
     * patron que [fr.frux.feature.harvestdetail.HarvestDetailViewModel.loadTree] : un échec ici
     * n'affiche AUCUNE erreur de fiche, la section correspondante reste simplement absente.
     */
    private fun loadHarvests() {
        viewModelScope.launch {
            harvestRepository.getHarvestsForTree(treeId).fold(
                onSuccess = { harvests ->
                    // Les passées (#435) restent listées, marquées et rangées après les à-venir.
                    val active =
                        harvests
                            .filter { it.status == HarvestStatus.PLANNED || it.status == HarvestStatus.ONGOING }
                            .sortedWith(compareBy<Harvest> { it.expired }.thenBy { it.scheduledDate })
                            .map { it.toTreeHarvestItem() }
                    _state.update { it.copy(activeHarvests = active) }
                },
                onFailure = { error ->
                    Logger.d(TAG, "⚠️ Récoltes indisponibles pour l'arbre $treeId : ${error.message}")
                },
            )
        }
    }

    /**
     * Pose une observation de statut sur le terrain (optimiste + pending, rollback sur échec) :
     * Un succès recharge la fiche (`loadTreeDetail`) — le
     * pseudo affiché dans [TreeObservation.observerUsername] et le statut affiché (`tree.status`)
     * sont réalignés depuis le serveur, jamais fabriqués localement.
     */
    @OptIn(ExperimentalTime::class)
    fun onObserveStatus(status: ObservableTreeStatus) {
        val currentTree = _state.value.tree
        if (!requireLogin(TreeDetailLoginPrompt.ObserveStatus) || currentTree == null) return
        if (_state.value.isObservationPending) return

        val previousStatus = currentTree.myObservationStatus
        val previousObservation = currentTree.latestObservation
        val previousSighting = currentTree.sighting
        val previousLastObservedAtMillis = currentTree.lastObservedAtMillis
        val previousSightingNowMillis = currentTree.sightingNowMillis
        val previousStatusStale = currentTree.statusStale
        val nowMillis = now().toEpochMilliseconds()

        _state.update {
            it.copy(
                tree =
                    currentTree.copy(
                        myObservationStatus = status,
                        latestObservation = TreeObservation(status, nowMillis, observerUsername = null),
                        // Le passage humain (#420, ADR 0025) doit refléter ce geste dès l'optimisme :
                        // sinon la fiche annonce "personne n'est jamais passé" pendant que
                        // l'observation qu'on vient de poser dit "observé à l'instant".
                        sighting = TreeSighting.SEEN_RECENTLY,
                        lastObservedAtMillis = nowMillis,
                        sightingNowMillis = nowMillis,
                        statusStale = false,
                    ),
                isObservationPending = true,
                observationError = null,
            )
        }

        viewModelScope.launch {
            treeRepository.observeTreeStatus(treeId, status).fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Observation '$status' persistée (tree $treeId)")
                    _state.update { it.copy(isObservationPending = false) }
                    loadTreeDetail()
                },
                onFailure = { e ->
                    Logger.e(TAG, "❌ Observation '$status' échouée", e)
                    _state.update {
                        it.copy(
                            tree =
                                it.tree?.copy(
                                    myObservationStatus = previousStatus,
                                    latestObservation = previousObservation,
                                    sighting = previousSighting,
                                    lastObservedAtMillis = previousLastObservedAtMillis,
                                    sightingNowMillis = previousSightingNowMillis,
                                    statusStale = previousStatusStale,
                                ),
                            isObservationPending = false,
                            observationError = e.asAppError(),
                        )
                    }
                },
            )
        }
    }

    /**
     * Bascule un trait (ajoute si absent, retire si posé par l'utilisateur courant) : optimistic
     * update + pending par kind, rollback sur échec. Même patron que [onReactionClick] historique,
     * mais togglable dans les deux sens (contrairement à l'ancien ajout seul).
     */
    fun onTraitClick(kind: TreeTraitKind) {
        val currentTree = _state.value.tree
        if (!requireLogin(TreeDetailLoginPrompt.AddTrait) || currentTree == null) return
        val trait = currentTree.traits.firstOrNull { it.kind == kind }
        if (kind in _state.value.pendingTraits || trait == null) return
        val adding = !trait.mine

        _state.update {
            it.copy(
                tree =
                    currentTree.copy(
                        traits =
                            currentTree.traits.map { t ->
                                if (t.kind == kind) {
                                    t.copy(
                                        mine = adding,
                                        count = if (adding) t.count + 1 else (t.count - 1).coerceAtLeast(0),
                                    )
                                } else {
                                    t
                                }
                            },
                    ),
                pendingTraits = it.pendingTraits + kind,
                traitError = null,
            )
        }

        viewModelScope.launch {
            val result =
                if (adding) treeRepository.addTreeTrait(treeId, kind) else treeRepository.removeTreeTrait(treeId, kind)
            result.fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Trait '$kind' ${if (adding) "ajouté" else "retiré"} (tree $treeId)")
                    _state.update { it.copy(pendingTraits = it.pendingTraits - kind) }
                },
                onFailure = { e ->
                    Logger.e(TAG, "❌ Trait '$kind' échoué", e)
                    _state.update {
                        it.copy(
                            tree =
                                it.tree?.copy(
                                    traits =
                                        it.tree.traits.map { t ->
                                            if (t.kind == kind) {
                                                t.copy(
                                                    mine = !adding,
                                                    count = if (adding) (t.count - 1).coerceAtLeast(0) else t.count + 1,
                                                )
                                            } else {
                                                t
                                            }
                                        },
                                ),
                            pendingTraits = it.pendingTraits - kind,
                            traitError = e.asAppError(),
                        )
                    }
                },
            )
        }
    }

    /**
     * Bascule le favori. L'optimisme et le rollback vivent dans le [FavoritesStore] (source
     * unique) ; ici on ne garde que le message de refus, affiché en snackbar.
     */
    fun onFavoriteClick() {
        if (!requireLogin(TreeDetailLoginPrompt.Favorite)) return
        _state.update { it.copy(favoriteError = null) }
        viewModelScope.launch {
            favoritesStore.toggle(treeId).onFailure { error ->
                Logger.e(TAG, "❌ Bascule du favori refusée", error)
                _state.update { it.copy(favoriteError = error.asAppError()) }
            }
        }
    }

    /**
     * Signale un doute d'identification (#129 lot B, ADR 0015). Optimiste + rollback + snackbar,
     * même patron que [onFavoriteClick] : le doublon serveur (déjà signalé) est atteignable
     * seulement si l'état local a divergé, la garde [TreeDetail.reportedByMe] l'évite côté client.
     * Un rechargement complet suit le succès : `disputed`/le bandeau de dangerosité dépendent tous
     * deux du serveur, un patch local partiel les ferait diverger.
     */
    fun onReportClick() {
        val currentTree = _state.value.tree
        if (!requireLogin(TreeDetailLoginPrompt.Report) || currentTree == null) return
        if (currentTree.isOwnTree || currentTree.reportedByMe || _state.value.isReportPending) return

        _state.update {
            it.copy(
                tree = currentTree.copy(disputed = true, reportedByMe = true),
                isReportPending = true,
                reportError = null,
            )
        }

        viewModelScope.launch {
            treeRepository.reportTree(treeId).fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Signalement envoyé (tree $treeId)")
                    _state.update { it.copy(isReportPending = false) }
                    loadTreeDetail()
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Signalement échoué", error)
                    _state.update {
                        it.copy(
                            tree =
                                it.tree?.copy(
                                    disputed = currentTree.disputed,
                                    reportedByMe = currentTree.reportedByMe,
                                ),
                            isReportPending = false,
                            reportError = error.asAppError(),
                        )
                    }
                },
            )
        }
    }

    /** Retire le signalement de l'utilisateur courant. Même patron que [onReportClick]. */
    fun onWithdrawReportClick() {
        val currentTree = _state.value.tree ?: return
        if (!currentTree.reportedByMe || _state.value.isReportPending) return

        _state.update {
            it.copy(
                tree = currentTree.copy(reportedByMe = false),
                isReportPending = true,
                reportError = null,
            )
        }

        viewModelScope.launch {
            treeRepository.withdrawTreeReport(treeId).fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Signalement retiré (tree $treeId)")
                    _state.update { it.copy(isReportPending = false) }
                    loadTreeDetail()
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Retrait du signalement échoué", error)
                    _state.update {
                        it.copy(
                            tree = it.tree?.copy(reportedByMe = currentTree.reportedByMe),
                            isReportPending = false,
                            reportError = error.asAppError(),
                        )
                    }
                },
            )
        }
    }

    /**
     * Signale l'arbre disparu (#129 lot C, ADR 0015). Même patron que [onReportClick] : optimiste,
     * kind `"GONE"` — indépendant de `reportedByMe`/`disputed` porté par MISIDENTIFIED, les deux
     * signalements coexistent sur le même arbre.
     */
    fun onReportGoneClick() {
        val currentTree = _state.value.tree
        if (!requireLogin(TreeDetailLoginPrompt.Report) || currentTree == null) return
        if (currentTree.isOwnTree || currentTree.reportedGoneByMe || _state.value.isReportPending) return

        _state.update {
            it.copy(
                tree = currentTree.copy(disputed = true, reportedGoneByMe = true),
                isReportPending = true,
                reportError = null,
            )
        }

        viewModelScope.launch {
            treeRepository.reportTree(treeId, kind = "GONE").fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Signalement de disparition envoyé (tree $treeId)")
                    _state.update { it.copy(isReportPending = false) }
                    loadTreeDetail()
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Signalement de disparition échoué", error)
                    _state.update {
                        it.copy(
                            tree =
                                it.tree?.copy(
                                    disputed = currentTree.disputed,
                                    reportedGoneByMe = currentTree.reportedGoneByMe,
                                ),
                            isReportPending = false,
                            reportError = error.asAppError(),
                        )
                    }
                },
            )
        }
    }

    /** Retire le signalement de disparition de l'utilisateur courant. Même patron que [onWithdrawReportClick]. */
    fun onWithdrawGoneReportClick() {
        val currentTree = _state.value.tree ?: return
        if (!currentTree.reportedGoneByMe || _state.value.isReportPending) return

        _state.update {
            it.copy(
                tree = currentTree.copy(reportedGoneByMe = false),
                isReportPending = true,
                reportError = null,
            )
        }

        viewModelScope.launch {
            treeRepository.withdrawTreeReport(treeId, kind = "GONE").fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Signalement de disparition retiré (tree $treeId)")
                    _state.update { it.copy(isReportPending = false) }
                    loadTreeDetail()
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Retrait du signalement de disparition échoué", error)
                    _state.update {
                        it.copy(
                            tree = it.tree?.copy(reportedGoneByMe = currentTree.reportedGoneByMe),
                            isReportPending = false,
                            reportError = error.asAppError(),
                        )
                    }
                },
            )
        }
    }

    /** Créateur seul : maintient l'identification, résout tous les signalements ouverts. */
    fun onMaintainIdentificationClick() {
        val currentTree = _state.value.tree ?: return
        if (!currentTree.isOwnTree || !currentTree.disputed || _state.value.isReportPending) return

        _state.update {
            it.copy(
                tree = currentTree.copy(disputed = false),
                isReportPending = true,
                reportError = null,
            )
        }

        viewModelScope.launch {
            treeRepository.resolveTreeReports(treeId).fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Identification maintenue (tree $treeId)")
                    _state.update { it.copy(isReportPending = false) }
                    loadTreeDetail()
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Maintien de l'identification échoué", error)
                    _state.update {
                        it.copy(
                            tree = it.tree?.copy(disputed = currentTree.disputed),
                            isReportPending = false,
                            reportError = error.asAppError(),
                        )
                    }
                },
            )
        }
    }

    /** Consomme l'erreur de signalement après affichage (snackbar). */
    fun consumeReportError() = _state.update { it.copy(reportError = null) }

    /** Consomme l'erreur d'observation après affichage (snackbar). */
    fun consumeObservationError() = _state.update { it.copy(observationError = null) }

    /** Consomme l'erreur de trait après affichage (snackbar). */
    fun consumeTraitError() = _state.update { it.copy(traitError = null) }

    /** Consomme l'erreur de favori après affichage (snackbar). */
    fun consumeFavoriteError() = _state.update { it.copy(favoriteError = null) }

    /** Consomme l'erreur de refresh silencieux après affichage (snackbar, #249). */
    fun consumeRefreshError() = _state.update { it.copy(refreshFailed = false) }

    /**
     * Consomme l'invite de connexion après affichage (snackbar, #264). One-shot porté dans l'état
     * du ViewModel plutôt qu'un `remember` d'écran (même patron que `MapViewModel.showLoginPrompt`) :
     * Nav3 détruit les effets locaux de l'écran à chaque recomposition de l'`entryProvider`.
     */
    fun consumeLoginPrompt() = _state.update { it.copy(loginPrompt = null) }

    /** Vraie si un utilisateur est connecté ; sinon poste l'invite de connexion (#264). */
    private fun requireLogin(prompt: TreeDetailLoginPrompt): Boolean {
        if (currentUserIdProvider() != null) return true
        _state.update { it.copy(loginPrompt = prompt) }
        return false
    }

    /** Envoie une photo préparée par core-media, puis recharge la fiche (le héro vient du serveur). */
    fun uploadPhoto(image: PickedImage) {
        if (currentUserIdProvider() == null) {
            _state.update { it.copy(loginPrompt = TreeDetailLoginPrompt.AddPhoto) }
            return
        }
        if (state.value.isUploadingPhoto) return
        _state.update { it.copy(isUploadingPhoto = true, photoMessage = null) }
        viewModelScope.launch {
            val prepared = image.prepareForUpload()
            treeRepository.uploadPhoto(treeId, prepared.bytes, prepared.contentType).fold(
                onSuccess = {
                    _state.update { s -> s.copy(isUploadingPhoto = false, photoMessage = TreeDetailPhotoMessage.Added) }
                    loadTreeDetail()
                },
                onFailure = { error ->
                    Logger.e(TAG, "Échec de l'upload de photo", error)
                    val failed = TreeDetailPhotoMessage.Failed(error.asAppError())
                    _state.update { s -> s.copy(isUploadingPhoto = false, photoMessage = failed) }
                },
            )
        }
    }

    /** Consomme le message photo après affichage (snackbar). */
    fun consumePhotoMessage() = _state.update { it.copy(photoMessage = null) }

    /**
     * Signale la photo affichée (#223 lot D, ADR 0015 réduit) : masquage automatique et immédiat
     * au premier signalement (N=1) côté serveur. Pas d'optimistic update — contrairement à
     * [onReportClick], la galerie affichée dépend entièrement de ce que le serveur garde ; un
     * [loadTreeDetail] après succès la reflète.
     *
     * L'id vient du pager (#247). La garde sur la galerie courante évite d'envoyer au serveur un
     * id qui ne peut venir que d'un état périmé.
     */
    fun reportPhoto(photoId: String) {
        if (!requireLogin(TreeDetailLoginPrompt.ReportPhoto) || _state.value.isReportingPhoto) return
        if (_state.value.tree
                ?.photos
                .orEmpty()
                .none { it.id == photoId }
        ) {
            return
        }

        _state.update { it.copy(isReportingPhoto = true, photoMessage = null) }

        viewModelScope.launch {
            treeRepository.reportPhoto(photoId).fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Photo signalée (photo $photoId)")
                    _state.update { it.copy(isReportingPhoto = false, photoMessage = TreeDetailPhotoMessage.Reported) }
                    loadTreeDetail()
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Signalement de photo échoué", error)
                    val failed = TreeDetailPhotoMessage.Failed(error.asAppError())
                    _state.update { it.copy(isReportingPhoto = false, photoMessage = failed) }
                },
            )
        }
    }

    /**
     * Gestes de l'ADR 0029 (#515) sur l'auteur et la description, voir [TreeDetailModerationController]
     * (extrait de ce ViewModel : ratchet detekt LargeClass).
     */
    private val moderationController =
        TreeDetailModerationController(
            treeId = treeId,
            treeRepository = treeRepository,
            userRepository = userRepository,
            scope = viewModelScope,
            state = _state,
            requireLogin = ::requireLogin,
        )

    /** Vrai si un compte est connecté, sinon pose l'invite (#264). L'écran l'appelle avant
     *  d'ouvrir la feuille de signalement ou la confirmation de masquage. */
    fun requestModeration(): Boolean = moderationController.requestModeration()

    /** Signale la description (ADR 0029) : file de triage, aucun effet visible sur la fiche. */
    fun onReportDescriptionClick() = moderationController.onReportDescriptionClick()

    /** Signale l'auteur au back-office. [reason] voyage par son `name` (code serveur). */
    fun onReportCreator(
        reason: UserReportReason,
        details: String?,
    ) = moderationController.onReportCreator(reason, details)

    /** Masque les contributions de l'auteur pour soi (ADR 0029). L'écran revient ensuite à la carte. */
    fun onBlockCreatorConfirmed() = moderationController.onBlockCreatorConfirmed()

    fun consumeModerationMessage() = moderationController.consumeModerationMessage()

    /**
     * Supprime une de ses propres photos (#247). Même absence d'optimisme que le signalement, et
     * même raison : c'est le rechargement qui dit quelle photo devient le héro. L'action n'est
     * offerte par l'UI que sur ses propres photos ; le 403 serveur reste la borne finale.
     */
    fun deletePhoto(photoId: String) {
        if (_state.value.isDeletingPhoto) return
        if (_state.value.tree
                ?.photos
                .orEmpty()
                .none { it.id == photoId }
        ) {
            return
        }

        _state.update { it.copy(isDeletingPhoto = true, photoMessage = null) }

        viewModelScope.launch {
            treeRepository.deletePhoto(photoId).fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Photo supprimée (photo $photoId)")
                    _state.update { it.copy(isDeletingPhoto = false, photoMessage = TreeDetailPhotoMessage.Deleted) }
                    loadTreeDetail()
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Suppression de photo échouée", error)
                    val failed = TreeDetailPhotoMessage.Failed(error.asAppError())
                    _state.update { it.copy(isDeletingPhoto = false, photoMessage = failed) }
                },
            )
        }
    }

    /**
     * Supprime l'arbre (propriétaire seul, #224). Pas d'optimisme (contrairement à
     * `onReactionClick`) : action destructive et rare, un spinner suffit. Succès → événement de
     * navigation arrière (la fiche disparaît, pas de state à retoucher). Échec → message affiché
     * (snackbar). Depuis le cancel-on-delete (#192), la suppression n'est plus jamais refusée pour
     * une récolte active : le serveur l'annule et notifie les participants.
     */
    fun onDeleteConfirmed() {
        if (_state.value.isDeleting) return
        _state.update { it.copy(isDeleting = true, deleteError = null) }

        viewModelScope.launch {
            treeRepository.deleteTree(treeId).fold(
                onSuccess = {
                    Logger.i(TAG, "✅ Arbre supprimé (tree $treeId)")
                    _state.update { it.copy(isDeleting = false) }
                    _events.send(TreeDetailEvent.Deleted)
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Suppression de l'arbre échouée", error)
                    _state.update { it.copy(isDeleting = false, deleteError = error.asAppError()) }
                },
            )
        }
    }

    /** Consomme l'erreur de suppression après affichage (snackbar). */
    fun consumeDeleteError() = _state.update { it.copy(deleteError = null) }
}

/**
 * State for Tree Detail screen.
 */
data class TreeDetailState(
    val tree: TreeDetail? = null,
    val isLoading: Boolean = false,
    /** Catégorie d'échec du chargement (#176) ; le libellé est résolu par l'écran. */
    val error: AppError? = null,
    /**
     * L'arbre n'existe pas ou n'est plus visible (404, #479) : identifiant faux, arbre masqué par
     * la modération (ADR 0015) ou supprimé. Un état expliqué avec un retour à la carte, pas
     * l'écran d'erreur générique, dont le « Réessayer » ne pourrait rien donner.
     */
    val notFound: Boolean = false,
    /**
     * Un refresh silencieux a échoué (#249) : à annoncer en snackbar, l'ancien [tree] reste
     * visible. Un booléen et non une catégorie — la cause de l'échec n'entre pas dans le libellé,
     * qui parle de l'actualisation, pas du transport.
     */
    val refreshFailed: Boolean = false,
    /** `true` pendant l'appel d'observation de statut en vol (garde anti double-tap). */
    val isObservationPending: Boolean = false,
    /** Échec (snackbar) quand le serveur refuse une observation de statut. */
    val observationError: AppError? = null,
    /** Kinds de trait dont la bascule (ajout/retrait) est en cours d'appel. */
    val pendingTraits: Set<TreeTraitKind> = emptySet(),
    /** Échec (snackbar) quand le serveur refuse la bascule d'un trait. */
    val traitError: AppError? = null,
    /** Favori de l'utilisateur courant ; false pour un visiteur anonyme. */
    val isFavorite: Boolean = false,
    /** Échec (snackbar) quand le serveur refuse le basculement du favori. */
    val favoriteError: AppError? = null,
    /** `true` pendant un appel de signalement/retrait/maintien en vol (#129 lot B). */
    val isReportPending: Boolean = false,
    /** Échec (snackbar) quand le serveur refuse une action de signalement. */
    val reportError: AppError? = null,
    /** `true` pendant l'envoi d'une photo (#223 lot C). */
    val isUploadingPhoto: Boolean = false,
    /** Confirmation ou refus d'une action photo, à afficher (snackbar) puis consommer. */
    val photoMessage: TreeDetailPhotoMessage? = null,
    /** `true` pendant l'envoi d'un signalement de photo (#223 lot D). Garde anti double-tap. */
    val isReportingPhoto: Boolean = false,
    /** `true` pendant la suppression d'une photo (#247). Garde anti double-tap. */
    val isDeletingPhoto: Boolean = false,
    /** `true` pendant la suppression de l'arbre (#224). Garde anti double-tap. */
    val isDeleting: Boolean = false,
    /** Échec (snackbar) quand la suppression est refusée. */
    val deleteError: AppError? = null,
    /**
     * Récoltes prévues sur cet arbre (#226), filtrées `PLANNED`/`ONGOING` — les terminées
     * n'intéressent pas la fiche. Chargement non bloquant : reste vide sur échec, jamais d'erreur
     * affichée (cf. [TreeDetailViewModel.loadHarvests]).
     */
    val activeHarvests: List<TreeHarvestItem> = emptyList(),
    /**
     * Invite de connexion (#264) à afficher (snackbar) puis consommer, posée quand un visiteur
     * anonyme tente une action qui exige un compte. `null` = pas d'invite en attente.
     */
    val loginPrompt: TreeDetailLoginPrompt? = null,
    /** `true` pendant un geste de l'ADR 0029 en vol (garde anti double-tap). */
    val isModerationPending: Boolean = false,
    /** Retour d'un geste de modération (#515), à afficher en snackbar puis consommer. */
    val moderationMessage: UserModerationMessage? = null,
    /** Feuilles de correction, message « Annuler » et mise en évidence du bloc Identité. */
    val identity: IdentityUi = IdentityUi(),
    /** Photos de cet arbre dont l'envoi a échoué (#633), pour « Réessayer » et « Retirer ». */
    val failedPhotoIds: List<String> = emptyList(),
)

/**
 * Ligne affichée dans la section « Récoltes prévues » de la fiche arbre (#226). Présentation
 * minimale — le clic navigue vers la fiche récolte complète, seule source de vérité pour le
 * reste (participants, actions organisateur…).
 */
data class TreeHarvestItem(
    val id: Long,
    val title: String,
    val dateLabel: String,
    /** Jour du mois (« 12 ») de la tuile-date. */
    val dayLabel: String,
    /** Mois abrégé (« SEPT ») de la tuile-date. */
    val monthLabel: String,
    val status: HarvestStatus,
    /** Date dépassée (#435) : libellé « Passée » à la place du statut. */
    val expired: Boolean = false,
)

/** Mappe une [Harvest] domaine vers sa présentation compacte sur la fiche arbre. */
private fun Harvest.toTreeHarvestItem(): TreeHarvestItem =
    TreeHarvestItem(
        id = id,
        title = description?.takeIf { it.isNotBlank() } ?: "Récolte du ${formatHarvestShortDate(scheduledDate)}",
        dateLabel = formatHarvestShortDate(scheduledDate),
        dayLabel = harvestDayOfMonth(scheduledDate),
        monthLabel = harvestMonthAbbrev(scheduledDate),
        status = status,
        expired = expired,
    )

/**
 * Geste qui a exigé un compte (#264, typé en #176) : l'écran choisit le libellé de l'invite, le
 * ViewModel ne connaît que l'intention.
 */
enum class TreeDetailLoginPrompt {
    ObserveStatus,
    AddTrait,
    Favorite,
    Report,
    AddPhoto,
    ReportPhoto,
    Moderate,
}

/** Retour d'une action sur la galerie (#223, #247), typé : le libellé est résolu par l'écran. */
sealed interface TreeDetailPhotoMessage {
    data object Added : TreeDetailPhotoMessage

    data object Reported : TreeDetailPhotoMessage

    data object Deleted : TreeDetailPhotoMessage

    data class Failed(
        val error: AppError,
    ) : TreeDetailPhotoMessage
}

/** Événement one-shot de suppression (#224) : la fiche navigue en arrière, elle est détruite. */
sealed interface TreeDetailEvent {
    data object Deleted : TreeDetailEvent

    /** Une correction exige un compte : l'écran ouvre la connexion. */
    data object LoginRequired : TreeDetailEvent
}

/**
 * Detailed tree information.
 */
data class TreeDetail(
    val id: Long,
    val speciesName: String,
    /** Identifiant d'espèce, pré-sélection de la feuille de correction de l'identité. */
    val speciesId: Long,
    /** Nom scientifique, affiché en italique sous le nom courant du bloc Identité. */
    val scientificName: String,
    val status: FruxStatus,
    val photoUrl: String?,
    /**
     * Glyphe de l'espèce, affiché à la place du cadre vide quand l'arbre n'a aucune photo (#389).
     * Un pictogramme et jamais une image réaliste : il ne peut pas passer pour une photo de cet
     * arbre ni servir à l'identifier (ADR 0015). `null` : repli générique de la galerie.
     */
    val speciesGlyph: ImageVector? = null,
    /**
     * Photo d'illustration de l'espèce (#389), posée par [fr.frux.feature.treedetail.toTreeDetail]
     * seulement si l'arbre n'a aucune photo réelle (ni galerie, ni [photoUrl]). Disparaît d'elle-même
     * dès qu'une vraie photo existe, sans code supplémentaire côté écran.
     */
    val speciesPhoto: SpeciesPhoto? = null,
    val description: String?,
    val latitude: Double,
    val longitude: Double,
    val createdBy: Long,
    val creatorUsername: String?, // Vrai pseudo (backend) ; null si le backend ne l'a pas fourni
    // Timestamp in milliseconds
    val createdAt: Long,
    /** Traits posés par les cueilleurs (accès difficile, délicieux), toujours les deux kinds (count 0 si absent). */
    val traits: List<TreeTrait> = emptyList(),
    /** Dernière observation de statut, tous auteurs confondus. `null` tant qu'aucune n'a été posée. */
    val latestObservation: TreeObservation? = null,
    /** Dernière observation de l'utilisateur courant. `null` s'il n'a jamais observé cet arbre. */
    val myObservationStatus: ObservableTreeStatus? = null,
    val ripenessLabel: String, // Libellé dérivé du statut (présentation, pas une donnée fabriquée)
    val certaintyLabel: String,
    /** Niveau de certitude brut (domaine) : pilote la jauge et la pastille courte du bloc Confiance. */
    val certainty: Certainty = Certainty.TO_CONFIRM,
    val inSeasonMonths: Set<Int>,
    val soonMonths: Set<Int>,
    val currentMonth: Int,
    val seasonLabel: String?,
    val hazard: TreeHazard = TreeHazard.None,
    /**
     * Consigne de préparation de la partie récoltée (`REQUIRES_PREPARATION`/`TOXIC_RAW`), bloc
     * récolte, palette neutre. Indépendante de [hazard] : c'est un fait culinaire, pas un niveau
     * d'alerte — le sureau noir affiche « toxique cru » ET sa ligne « ne pas confondre » (ADR 0015,
     * déroulé sureau).
     */
    val preparationLabel: String? = null,
    /** Contesté par les pairs (#129 lot B, ADR 0015) — dérivé serveur, jamais de `certainty`. */
    val disputed: Boolean = false,
    /**
     * Libellé du bandeau contesté, différencié par kind de signalement ouvert (#208). Toujours
     * renseigné quand [disputed] : retombe sur le libellé générique si les kinds manquent
     * (cache hors ligne, kind inconnu du client).
     */
    val disputedLabel: String = DISPUTED_GENERIC_LABEL,
    /** `true` si l'utilisateur courant a un signalement MISIDENTIFIED encore ouvert. */
    val reportedByMe: Boolean = false,
    /** `true` si l'utilisateur courant a un signalement GONE encore ouvert (#129 lot C, ADR 0015). */
    val reportedGoneByMe: Boolean = false,
    /** `true` si l'utilisateur courant est le créateur de l'arbre — masque "Signaler un doute",
     *  affiche "Maintenir mon identification" quand [disputed]. */
    val isOwnTree: Boolean = false,
    /**
     * L'auteur peut être signalé ou masqué (ADR 0029) : ni son propre arbre, ni un compte système
     * d'import, ni un auteur sans pseudo connu. Vrai aussi pour un visiteur anonyme, à qui le
     * geste propose de se connecter.
     */
    val canModerateCreator: Boolean = false,
    /**
     * Arbre venu d'un import en masse (OpenStreetMap ou une collectivité, ADR 0010), jamais
     * adopté par un cueilleur connu de l'écran : la fiche dit « Importé de <source> » avec la
     * licence plutôt que le pseudo technique du compte d'import (#476, #501).
     */
    val importSource: ImportedTreeSource? = null,
    /**
     * Galerie de l'arbre (#247), dans l'ordre du serveur : la première est le héro. Vide tant
     * qu'aucune photo n'a été versée — l'écran retombe alors sur [photoUrl] puis sur le vecteur.
     */
    val photos: List<TreeDetailPhoto> = emptyList(),
    /** Passage humain devant l'arbre (#420, ADR 0025) : dérivé une fois pour toutes par `treeSighting`. */
    val sighting: TreeSighting = TreeSighting.NEVER_SEEN,
    /** Support de [sighting] pour les niveaux datés ; `null` quand le niveau vient de la seule photo. */
    val lastObservedAtMillis: Long? = null,
    /**
     * Horloge qui a servi à dériver [sighting] (#420) : reprise telle quelle pour formater la date
     * relative de [lastObservedAtMillis], jamais une seconde lecture de `Clock.System.now()` — le
     * niveau et la date affichée doivent toujours provenir de la même horloge.
     */
    val sightingNowMillis: Long = 0L,
    /**
     * Statut de terrain périmé (#604) : le badge suit déjà le calendrier, la ligne « dernière
     * observation » le dit en ton atténué pour que la date affichée n'ait pas l'air d'un fait présent.
     */
    val statusStale: Boolean = false,
    /** Dernière correction de l'identité (pied du bloc Identité), `null` si jamais corrigé ou API sans correction. */
    val lastCorrection: TreeCorrectionMark? = null,
)

/**
 * Une photo de la galerie et ce que l'utilisateur courant a le droit d'en faire (#247). Les deux
 * droits s'excluent : on signale la photo d'un autre (#223 lot D), on supprime la sienne, et un
 * visiteur anonyme ne voit ni l'une ni l'autre action.
 *
 * [url] est la miniature de la grille, [fullUrl] la pleine résolution que la visionneuse zoome (#686).
 */
data class TreeDetailPhoto(
    val id: String,
    val url: String,
    val canDelete: Boolean,
    val canReport: Boolean,
    val fullUrl: String = url,
)

/**
 * Alerte sosie affichée sur la fiche (ADR 0015), résolue par
 * [fr.frux.feature.treedetail.resolveHazard]. `LookalikeWarning` est le seul niveau rendu en rouge
 * (tonalité `error` du thème). La consigne de préparation est portée séparément par
 * [TreeDetail.preparationLabel] et peut coexister avec chacun de ces niveaux.
 */
sealed class TreeHazard {
    /** Rien à afficher — absence de documentation, jamais présentée comme une garantie de sécurité. */
    data object None : TreeHazard()

    /** Sosie(s) documenté(s), identification sûre — ligne repliée « Ne pas confondre avec… ». */
    data class LookalikeInfo(
        val lookalikes: List<SpeciesLookalike>,
    ) : TreeHazard()

    /** Sosie `LETHAL` et identification non sûre — bandeau rouge en tête de fiche, seul rouge de l'app. */
    data class LookalikeWarning(
        val lookalikes: List<SpeciesLookalike>,
    ) : TreeHazard()
}
